<template>
	<div class="catalog-search-input" :style="style" :class="{ disabled, 'is-multiple': multiple, 'is-stacked': stackedChips }">
		<div
			v-if="multiple"
			class="field-input multi-input"
			:class="{ empty: !hasSelection && !inputValue, 'stacked-layout': stackedChips }"
			@click="focusInput"
		>
			<span v-for="option in selectedOptions" :key="option.id" class="multi-chip">
				<span class="chip-label">{{ getOptionName(option) }}</span>
				<button
					type="button"
					class="chip-remove"
					:disabled="disabled"
					@mousedown.prevent.stop
					@click.stop="removeOption(option)"
					aria-label="Remove selection"
				>
					×
				</button>
			</span>
			<input
				:id="inputId"
				type="text"
				role="combobox"
				:aria-label="placeholder"
				aria-autocomplete="list"
				:aria-expanded="showDropdown"
				:aria-controls="listId"
				:aria-activedescendant="highlightedIndex >= 0 ? `${listId}-${highlightedIndex}` : undefined"
				:value="inputValue"
				:disabled="disabled"
				@input="handleInput"
				@focus="handleFocus"
				@click="openDropdown"
				@blur="handleBlur"
				@keydown.tab="hideDropdown"
				@keydown.down.prevent="navigateDown"
				@keydown.up.prevent="navigateUp"
				@keydown.enter.prevent="selectHighlightedOption"
				@keydown.esc="hideDropdown"
				:placeholder="hasSelection ? '' : placeholder"
				class="multi-input-field"
				ref="inputRef"
			/>
		</div>
		<button
			v-else-if="selectionOnly"
			:id="inputId"
			type="button"
			role="combobox"
			:aria-label="placeholder"
			aria-haspopup="listbox"
			:aria-expanded="showDropdown"
			:aria-controls="listId"
			:aria-activedescendant="highlightedIndex >= 0 ? `${listId}-${highlightedIndex}` : undefined"
			:disabled="disabled"
			@click="showDropdown ? hideDropdown() : openDropdown()"
			@blur="handleBlur"
			@keydown.tab="hideDropdown"
			@keydown.down.prevent="navigateDown"
			@keydown.up.prevent="navigateUp"
			@keydown.enter.prevent="selectHighlightedOption"
			@keydown.space.prevent="selectHighlightedOption"
			@keydown.esc.prevent="hideDropdown"
			class="field-input select-trigger"
			ref="inputRef"
		>
			<span :class="{ 'select-placeholder': !selectedOptionName }">{{ selectedOptionName || placeholder }}</span>
		</button>
		<input
			v-else
			:id="inputId"
			type="text"
			role="combobox"
			:aria-label="placeholder"
			aria-autocomplete="list"
			:aria-expanded="showDropdown"
			:aria-controls="listId"
			:aria-activedescendant="highlightedIndex >= 0 ? `${listId}-${highlightedIndex}` : undefined"
			:value="displayValue"
			:disabled="disabled"
			@input="handleInput"
			@focus="handleFocus"
			@click="openDropdown"
			@blur="handleBlur"
			@keydown.tab="hideDropdown"
			@keydown.down.prevent="navigateDown"
			@keydown.up.prevent="navigateUp"
			@keydown.enter.prevent="selectHighlightedOption"
			@keydown.esc="hideDropdown"
			:placeholder="placeholder"
			class="field-input"
			:class="{ empty: !hasSelection }"
			ref="inputRef"
		/>
		<div v-if="!disabled && showDropdown && filteredOptions.length > 0" :class="dropdownClasses" :style="dropdownStyle">
			<ul :id="listId" role="listbox" :aria-multiselectable="multiple || undefined">
					<li
						v-for="(option, index) in filteredOptions"
						:key="option.id"
						:id="`${listId}-${index}`"
						role="option"
						:aria-selected="isSelected(option)"
						@mousedown.prevent
						@click="selectOption(option)"
						:class="{ 'is-highlighted': index === highlightedIndex, 'is-selected': isSelected(option) }"
						@mouseenter="highlightedIndex = index"
					>
						{{ getOptionName(option) }}
					</li>
			</ul>
		</div>
		<div v-else-if="!disabled && showDropdown && inputValue && filteredOptions.length === 0" :class="dropdownClasses" :style="dropdownStyle">
			No matching items found.
		</div>
	</div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { fitFloatingPanel, trackFloatingAnchor } from '@/utils/floatingPanel';

export default {
	name: 'CatalogSearchInput',
		props: {
			inputId: { type: String, default: undefined },
			useFixed: { type: Boolean, default: false },
			modelValue: {
				type: [String, Number, Array, null],
				default: null,
			},
		multiple: {
			type: Boolean,
			default: false,
		},
		selectionOnly: {
			type: Boolean,
			default: false,
		},
		stackedChips: {
			type: Boolean,
			default: false,
		},
	options: {
			type: Array,
			required: true,
		},
		placeholder: {
			type: String,
			default: 'Select an item',
		},
		fallbackLabel: {
			// Shown as the field value when the model id has no matching option
			// (e.g. a document stores plain text without a catalog link).
			type: String,
			default: '',
		},
		style: {
			type: Object,
			default: () => ({}),
		},
		disabled: {
			type: Boolean,
			default: false,
		},
	},
	emits: ['update:modelValue'],
	data() {
		return {
			listId: `catalog-options-${getCurrentInstance().uid}`,
			inputValue: '',
			showDropdown: false,
			highlightedIndex: -1,
			dropdownPosition: 'below',
			maxDropdownHeight: 220,
			dropdownStyle: {},
		};
	},
	mounted() {
		document.addEventListener('pointerdown', this.handleDocumentPointerDown, true);
	},
	deactivated() { this.hideDropdown(); },
	beforeUnmount() {
		document.removeEventListener('pointerdown', this.handleDocumentPointerDown, true);
		this.stopPositionTracking();
	},
		computed: {
			normalizedSelection() {
				if (!this.multiple) {
					return this.modelValue !== null && this.modelValue !== undefined ? [this.modelValue] : [];
				}
				return Array.isArray(this.modelValue) ? this.modelValue : [];
			},
			hasSelection() {
				return this.normalizedSelection.length > 0;
			},
			filteredOptions() {
				if ((!this.multiple && this.selectionOnly) || !this.inputValue) {
					return this.options;
				}
				const searchTerm = this.inputValue.toLowerCase();
				return this.options.filter((option) => this.getOptionName(option).toLowerCase().includes(searchTerm));
			},
			selectedOptions() {
				if (!this.normalizedSelection.length) return [];
				const ids = this.normalizedSelection.map((id) => String(id));
				const mapById = new Map(this.options.map((opt) => [String(opt?.id), opt]));
				return ids.map((id) => mapById.get(String(id))).filter(Boolean);
			},
			selectedOption() {
				if (this.multiple) return null;
				return this.options.find((opt) => this.matchesId(opt?.id, this.modelValue)) || null;
			},
			selectedOptionName() {
				if (this.multiple) {
					return this.selectedOptions.map((opt) => this.getOptionName(opt)).join(', ');
				}
				if (this.selectedOption) return this.getOptionName(this.selectedOption);
				return this.fallbackLabel || '';
			},
			displayValue() {
				return this.showDropdown ? this.inputValue : this.selectedOptionName;
			},
		dropdownClasses() {
			return {
				'dropdown-menu': true,
				'is-above': this.dropdownPosition === 'above',
				'no-results': this.showDropdown && this.inputValue && this.filteredOptions.length === 0,
			};
	},
	},
		methods: {
			handleDocumentPointerDown(event) {
				if (!this.showDropdown) return;
				const path = typeof event?.composedPath === 'function' ? event.composedPath() : [];
				const clickedInside = path.includes(this.$el) || this.$el?.contains(event?.target);
				this._pointerInside = clickedInside;
				if (clickedInside) return;
				this.hideDropdown();
				this.$refs.inputRef?.blur();
			},
			handleBlur(event) {
				// A touch on a non-focusable option can blur the input before click.
				// Keep the list mounted through taps and scrolling; outside presses and Tab close it.
				if (this.$el?.contains(event.relatedTarget)) return;
				if (this._pointerInside && (!event.relatedTarget || event.relatedTarget === document.body)) return;
				this.hideDropdown();
			},
			focusInput() {
				if (this.disabled) return;
				this.$refs.inputRef?.focus({ preventScroll: this.selectionOnly });
			},
			matchesId(optionId, selectedId) {
				if (optionId === null || optionId === undefined) return false;
				if (selectedId === null || selectedId === undefined) return false;
				return String(optionId) === String(selectedId);
			},
			isSelected(option) {
				const optionId = option?.id;
				if (this.selectionOnly && !this.multiple && optionId === null) return this.modelValue == null;
				if (optionId === null || optionId === undefined) return false;
				if (this.multiple) {
					return this.normalizedSelection.some((id) => this.matchesId(optionId, id));
				}
				return this.matchesId(optionId, this.modelValue);
			},
			getOptionName(option = {}) {
				return (
					option?.name ||
					option?.label ||
				option?.attributes?.name ||
				option?.attributes?.label ||
				'Untitled'
			);
		},
		stopPositionTracking() {
			this._stopPositionTracking?.();
			this._stopPositionTracking = null;
		},
		startPositionTracking() {
			this.stopPositionTracking();
			if (!this.useFixed || !this.showDropdown || !this.$refs.inputRef?.isConnected) return;
			this._stopPositionTracking = trackFloatingAnchor(this.$refs.inputRef, this.positionDropdown);
		},
		positionDropdown() {
			if (!this.useFixed || !this.showDropdown || !this.$el?.isConnected) return;
			const anchor = this.$refs.inputRef?.getBoundingClientRect();
			if (!anchor) return;
			this.dropdownStyle = fitFloatingPanel(anchor, {
				width: Math.max(anchor.width, 240), height: this.maxDropdownHeight,
			}, undefined, 4);
			this.dropdownStyle.maxHeight = `${Math.min(this.maxDropdownHeight, parseFloat(this.dropdownStyle.maxHeight))}px`;
		},
		getDropdownPosition() {
			if (!this.$refs.inputRef) return 'below';
			const inputRect = this.$refs.inputRef.getBoundingClientRect();
			const scrollContainer = this.$el.closest('.main-container') || document.documentElement;
			const containerRect = scrollContainer.getBoundingClientRect();
			const spaceBelow = containerRect.bottom - inputRect.bottom;
			const effectiveContainerTop = Math.max(containerRect.top, 0);
			const spaceAbove = inputRect.top - effectiveContainerTop;
			const minSpaceRequired = 50;
			if (spaceBelow < this.maxDropdownHeight && spaceAbove >= this.maxDropdownHeight && spaceAbove > spaceBelow) {
				return 'above';
			} else if (spaceBelow < this.maxDropdownHeight && spaceAbove >= minSpaceRequired && spaceAbove > spaceBelow) {
				return 'above';
			}
			return 'below';
		},
		openDropdown() {
			if (this.disabled) return;
			this.dropdownPosition = this.useFixed ? 'below' : this.getDropdownPosition();
			this.showDropdown = true;
			this.highlightedIndex = this.selectionOnly && !this.multiple && this.filteredOptions.length
				? Math.max(0, this.filteredOptions.findIndex((option) => this.isSelected(option)))
				: -1;
			this.$nextTick(this.startPositionTracking);
			if (this.selectionOnly && !this.multiple) this.scrollToHighlighted();
		},
			handleInput(event) {
				if (this.disabled) return;
				this.inputValue = event.target.value;
				this.openDropdown();
				if (!this.multiple && !this.inputValue && this.modelValue !== null) {
					this.$emit('update:modelValue', null);
				}
			},
			selectOption(option) {
				if (this.disabled) return;
				if (this.multiple) {
					const current = Array.isArray(this.modelValue) ? [...this.modelValue] : [];
					const existsIndex = current.findIndex((id) => this.matchesId(id, option.id));
					if (existsIndex >= 0) {
						current.splice(existsIndex, 1);
					} else {
						current.push(option.id);
					}
					this.$emit('update:modelValue', current);
					this.inputValue = '';
					this.openDropdown();
					return;
				}
				this.$emit('update:modelValue', option.id);
				this.inputValue = this.getOptionName(option);
				this.hideDropdown();
				if (this.selectionOnly) this.focusInput();
				else this.$refs.inputRef.blur();
			},
			removeOption(option) {
				if (this.disabled || !this.multiple) return;
				const current = Array.isArray(this.modelValue) ? [...this.modelValue] : [];
				const next = current.filter((id) => !this.matchesId(id, option.id));
				this.$emit('update:modelValue', next);
				this.openDropdown();
			},
			hideDropdown() {
				this._pointerInside = false;
				this.stopPositionTracking();
				if (!this.showDropdown) return;
				if (!this.multiple && this.inputValue !== this.selectedOptionName) {
					this.inputValue = this.selectedOptionName;
				}
				if (!this.multiple && this.modelValue === null && this.inputValue) {
					this.inputValue = '';
				}
				if (this.multiple) {
					this.inputValue = '';
				}
				this.showDropdown = false;
				this.highlightedIndex = -1;
			},
			handleFocus() {
				if (this.disabled) return;
				// Start from an empty query when nothing is linked (fallback label case)
				// so the full option list is available instead of a no-match state.
				this.inputValue = this.multiple || !this.selectedOption ? '' : this.selectedOptionName;
				this.openDropdown();
			},
		navigateDown() {
			if (this.disabled) return;
			if (!this.showDropdown) {
				this.openDropdown();
			} else if (this.filteredOptions.length > 0) {
				this.highlightedIndex = (this.highlightedIndex + 1) % this.filteredOptions.length;
				this.scrollToHighlighted();
			}
		},
		navigateUp() {
			if (!this.disabled && !this.showDropdown && this.selectionOnly && !this.multiple) {
				this.openDropdown();
				if (!this.hasSelection) this.highlightedIndex = this.filteredOptions.length - 1;
				this.scrollToHighlighted();
				return;
			}
			if (this.disabled || !this.showDropdown || !this.filteredOptions.length) return;
			this.highlightedIndex = (this.highlightedIndex - 1 + this.filteredOptions.length) % this.filteredOptions.length;
			this.scrollToHighlighted();
		},
		selectHighlightedOption() {
			if (this.disabled) return;
			if (!this.showDropdown && this.selectionOnly && !this.multiple) {
				this.openDropdown();
				return;
			}
			if (this.showDropdown && this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredOptions.length) {
				this.selectOption(this.filteredOptions[this.highlightedIndex]);
			} else if (this.showDropdown && !this.inputValue && this.filteredOptions.length > 0) {
				this.selectOption(this.filteredOptions[0]);
			} else {
				this.hideDropdown();
			}
		},
		scrollToHighlighted() {
			this.$nextTick(() => {
				const menu = this.$el.querySelector('.dropdown-menu');
				const list = menu?.querySelector('ul');
				const item = list?.children[this.highlightedIndex];
				if (!menu || !item) return;
				// Scroll only the options, never the table or page behind this popup.
				const menuRect = menu.getBoundingClientRect();
				const itemRect = item.getBoundingClientRect();
				if (itemRect.top < menuRect.top) menu.scrollTop -= menuRect.top - itemRect.top;
				else if (itemRect.bottom > menuRect.bottom) menu.scrollTop += itemRect.bottom - menuRect.bottom;
			});
		},
	},
		watch: {
			disabled(value) { if (value) this.hideDropdown(); },
			modelValue(newValue) {
				if (this.showDropdown) return;
				if (this.multiple) {
					this.inputValue = '';
					return;
				}
				if (newValue === null) {
					this.inputValue = '';
					return;
				}
				const selected = this.options.find((opt) => this.matchesId(opt?.id, newValue));
				if (selected) {
					this.inputValue = this.getOptionName(selected);
				}
			},
			options(newOptions) {
				if (this.multiple || this.modelValue === null || this.showDropdown) {
					return;
				}
				const selected = newOptions.find((opt) => this.matchesId(opt?.id, this.modelValue));
				if (selected) {
					this.inputValue = this.getOptionName(selected);
				}
			// If the selected option no longer exists, keep displaying the last known label
			// and let the user resolve it manually instead of emitting null automatically.
		},
	},
};
</script>

<style scoped>
.catalog-search-input {
	position: relative;
	display: block;
}

.field-input {
	width: 100%;
	border: var(--catalog-input-border, 1px solid var(--border-color, #d1d5db));
	border-radius: var(--catalog-input-border-radius, 4px);
	padding: var(--catalog-input-padding, 6px 10px);
	font-size: var(--catalog-input-font-size, 0.9rem);
	height: var(--catalog-input-height, 40px);
	background: var(--catalog-input-background, #fff);
	color: var(--catalog-input-color, var(--text-color, #111827));
	font-family: var(--catalog-input-font-family, inherit);
	box-shadow: var(--catalog-input-box-shadow, none);
	box-sizing: border-box;
}

.field-input:hover {
	border-color: var(--catalog-input-hover-border-color, var(--border-color, #d1d5db));
}

.field-input::placeholder {
	color: var(--text-color-secondary, #9ca3af);
	opacity: 0.6;
}

.field-input:focus {
	outline: none;
	border-color: var(--catalog-input-focus-border-color, var(--noc-blue, #3498db));
	box-shadow: var(--catalog-input-focus-box-shadow, none);
}

.select-trigger {
	text-align: left;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	cursor: pointer;
	touch-action: manipulation;
}

.select-trigger:disabled { cursor: default; }

.select-placeholder {
	color: var(--text-color-secondary, #9ca3af);
	opacity: 0.6;
}

.multi-input {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px;
	min-height: 40px;
	height: auto;
	cursor: text;
}

.multi-input.stacked-layout {
	flex-direction: column;
	align-items: stretch;
	gap: 4px;
}

.multi-input.stacked-layout .multi-chip {
	width: 100%;
	justify-content: space-between;
	box-sizing: border-box;
}

.multi-input.stacked-layout .multi-input-field {
	width: 100%;
	min-width: 100%;
}

.multi-input:focus-within {
	border-color: var(--noc-blue, #3498db);
}

.multi-input-field {
	flex: 1;
	min-width: 90px;
	border: none;
	background: transparent;
	padding: 0;
	height: auto;
	font-size: var(--catalog-input-font-size, 0.9rem);
	color: var(--text-color, #111827);
	outline: none;
}

.multi-input-field:focus {
	border: none !important;
	box-shadow: none;
	outline: none;
}

.multi-input-field::placeholder {
	color: var(--text-color-secondary, #9ca3af);
	opacity: 0.6;
}

.multi-chip {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 4px 8px;
	border-radius: 999px;
	background: var(--noc-blue-05, #eff6ff);
	color: var(--noc-blue, #0b5ae0);
	font-size: 0.85rem;
	font-weight: 600;
	white-space: nowrap;
}

.multi-chip .chip-remove {
	border: none;
	background: none;
	color: inherit;
	cursor: pointer;
	font-size: 0.9rem;
	line-height: 1;
	padding: 0;
}

.multi-chip .chip-remove:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.dropdown-menu {
	position: absolute;
	left: 0;
	width: 100%;
	max-height: 220px;
	overflow-y: auto;
	background: #ffffff; /* Solid background to avoid translucency */
	background-color: #ffffff;
	border: 1px solid var(--border-color, #d1d5db);
	border-radius: 6px;
	box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
	color: var(--text-color, #111827);
	opacity: 1;
	backdrop-filter: none;
	-webkit-backdrop-filter: none;
	z-index: 3000;
	margin-top: 4px;
}

.dropdown-menu.is-above {
	bottom: calc(100% + 4px);
	top: auto;
	margin-top: 0;
}

/* A slim scrollbar that floats inside the list, clear of its rounded corners, like on mobile.
   The app-wide scrollbar-width/color make Chromium ignore custom scrollbar styles, so reset them here. */
.dropdown-menu {
	scrollbar-width: auto;
	scrollbar-color: auto;
}

.dropdown-menu::-webkit-scrollbar {
	width: 12px;
}

.dropdown-menu::-webkit-scrollbar-track {
	margin: 6px 0;
	background: transparent;
}

.dropdown-menu::-webkit-scrollbar-thumb {
	min-height: 28px;
	border: 4px solid transparent;
	border-radius: 999px;
	background-color: rgb(94 97 112 / 26%);
	background-clip: padding-box;
}

.dropdown-menu::-webkit-scrollbar-thumb:hover {
	background-color: rgb(94 97 112 / 42%);
}

@supports not selector(::-webkit-scrollbar) {
	.dropdown-menu {
		scrollbar-width: thin;
		scrollbar-color: rgb(94 97 112 / 30%) transparent;
	}
}

.dropdown-menu ul {
	list-style: none;
	margin: 0;
	padding: 4px 0;
	max-height: none;
	overflow: visible;
}

	.dropdown-menu li {
		padding: 6px 12px;
		cursor: pointer;
		font-size: 0.9rem;
		background: transparent;
		color: var(--text-color, #111827);
	}

	.dropdown-menu li.is-selected {
		background: var(--noc-blue-05, #eff6ff);
		color: var(--noc-blue, #0b5ae0);
		font-weight: 600;
	}

	.dropdown-menu li.is-highlighted:not(.is-selected),
	.dropdown-menu li:not(.is-selected):hover {
		background: var(--noc-blue-05, #eff6ff);
	}

.dropdown-menu.no-results {
	padding: 8px 12px;
	color: var(--text-color-secondary, #9ca3af);
}
</style>
